import { Button, ButtonProps } from '@chakra-ui/react'
import { scoBackendUrl } from '@src/lib/startups-url'
import React, { forwardRef, useRef, useState } from 'react'

export function checkValidExtension({
  allowedExtensions,
  fileToUpload,
}: {
  allowedExtensions: string[]
  fileToUpload: File
}) {
  if (allowedExtensions.indexOf('*') !== -1) {
    return true
  }
  return allowedExtensions.some(ext => fileToUpload.name.toLowerCase().endsWith(ext.toLowerCase()))
}

export function checkFileSize({
  fileToUpload,
  maxSizeInBytes,
}: {
  fileToUpload: File
  maxSizeInBytes: number
}) {
  return fileToUpload.size < maxSizeInBytes
}

export async function uploadDocumentFile({
  file,
  uploadUrl = scoBackendUrl('/assets/upload-document'),
  storedFile = {
    attachmentType: null as string | null,
    attachmentId: null as string | null,
    contextKey: null as string | null,
  },
  maxSizeInBytes = 40000000,
  /**
   * When true, ask the server to accept unauthenticated / guest uploads
   * (homepage intake). Server still fails closed unless this flag is set.
   */
  bypassAuth = false,
  onSuccess = (url: string, storedFileId?: string) => console.log(url, storedFileId),
  onError = (err: any) => console.log(err),
}: {
  file: File
  uploadUrl?: string
  storedFile?: {
    attachmentType?: string | null
    attachmentId?: string | null
    contextKey?: string | null
  }
  maxSizeInBytes?: number
  bypassAuth?: boolean
  onSuccess?: (url: string, storedFileId?: string) => void
  onError?: (err: any) => void
}) {
  try {
    const body = new FormData()
    body.append('fileToUpload', file)
    // Only send attachment fields when set. FormData coerces null → the string "null",
    // which the server would persist and block later claim-to-Comment.
    if (storedFile?.attachmentType) {
      body.append('attachmentType', storedFile.attachmentType)
    }
    if (storedFile?.attachmentId) {
      body.append('attachmentId', storedFile.attachmentId)
    }
    if (storedFile?.contextKey) {
      body.append('contextKey', storedFile.contextKey)
    }
    body.append('maxSizeInBytes', `${maxSizeInBytes}`)
    if (bypassAuth) {
      body.append('allowUnauthenticated', 'true')
    }

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body,
      credentials: 'include',
    })

    const data = await response.json()

    if (data.error) {
      onError(data.error)
      return
    }

    if (data.url) {
      onSuccess(data.url, data.storedFileId)
    }
  } catch (err) {
    onError(err)
  }
}

export type UploadDocumentButtonProps = ButtonProps & {
  onSuccess?: (url: string, storedFileId?: string) => any
  onError?: (err: any) => any
  allowedExtensions?: string[]
  maxSizeInBytes?: number
  uploadUrl?: string
  /** Opt into guest / unauthenticated document uploads (server must allow). */
  bypassAuth?: boolean
  children?: React.ReactNode
  storedFile?: {
    attachmentType?: string
    attachmentId?: string
    contextKey?: string
  }
}

const UploadDocumentButton = forwardRef<HTMLButtonElement, UploadDocumentButtonProps>(
  (
    {
      onSuccess = (url: string, storedFileId?: string) => console.log(url, storedFileId),
      onError = err => console.log(err),
      allowedExtensions = ['*'],
      maxSizeInBytes = 40000000, // 40 MB,
      uploadUrl = scoBackendUrl('/assets/upload-document'),
      bypassAuth = false,
      children,
      storedFile = {
        attachmentType: null,
        attachmentId: null,
        contextKey: null,
      },
      ...buttonProps
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [isSuccessful, setIsSuccessful] = useState(false)

    async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
      const fileToUpload = e.target.files?.[0]

      if (!fileToUpload) {
        return
      }

      if (!checkValidExtension({ allowedExtensions, fileToUpload })) {
        onError(`Please select one of these file types: ${allowedExtensions.join(', ')}`)
        return
      }

      if (!checkFileSize({ fileToUpload, maxSizeInBytes })) {
        onError(`Please select a file that is less than ${maxSizeInBytes / 1000000}MB in size.`)
        return
      }

      setIsUploading(true)

      try {
        await uploadDocumentFile({
          file: fileToUpload,
          uploadUrl,
          storedFile,
          maxSizeInBytes,
          bypassAuth,
          onSuccess: (url, storedFileId) => {
            setIsSuccessful(true)
            setTimeout(() => setIsSuccessful(false), 500)
            if (fileInputRef.current) {
              fileInputRef.current.value = null
            }
            onSuccess(url, storedFileId)
          },
          onError: err => {
            if (fileInputRef.current) {
              fileInputRef.current.value = null
            }
            onError(err)
          },
        })
      } finally {
        setIsUploading(false)
      }
    }

    return (
      <span>
        <Button
          ref={ref}
          w={'fit-content'}
          {...buttonProps}
          loading={isUploading || isSuccessful}
          onClick={() => fileInputRef.current?.click()}
        >
          {children}
        </Button>
        <input ref={fileInputRef} style={{ display: 'none' }} type={'file'} onChange={onFileChange} />
      </span>
    )
  }
)

UploadDocumentButton.displayName = 'UploadDocumentButton'

export { UploadDocumentButton }
